import { useEffect, useMemo, useRef, useState } from 'react';
import { CircleDot } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import usePageMetadata from '../../hooks/usePageMetadata';
import { getFit } from '../../services/toleranceService';
import FitResult from './FitResult';
import { getInitialFitInputs, normalizeFitDesignationCase, parseFitInputs } from './fitDisplay';
import './FitsPage.css';
import { useUnits } from '../../units/UnitContext';
import { unitLabel } from '../../units/unitConversions';
import { useUnitText } from '../../units/useUnitText';

const REQUEST_DELAY_MS = 250;
const PAGE_TITLE = 'ISO 286 Fits Calculator | ASiJoLab';
const PAGE_DESCRIPTION =
  'Evaluate a known ISO 286 hole-and-shaft combination, dimensional limits, and tolerance zones.';

const FitsPage = () => {
  const [searchParams] = useSearchParams();
  const { unitSystem } = useUnits();
  const [initialInputs] = useState(() => getInitialFitInputs(searchParams));
  const [diameter, setDiameter, canonicalDiameter] = useUnitText(initialInputs.diameter, 'mm', unitSystem);
  const [hole, setHole] = useState(initialInputs.hole);
  const [shaft, setShaft] = useState(initialInputs.shaft);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const calculatorRef = useRef(null);
  const parsedInput = useMemo(
    () => parseFitInputs(
      { diameter: diameter ? String(canonicalDiameter(diameter)) : diameter, hole, shaft },
      { rangeUnitSystem: unitSystem }
    ),
    [canonicalDiameter, diameter, hole, shaft, unitSystem]
  );

  usePageMetadata(PAGE_TITLE, PAGE_DESCRIPTION);

  useEffect(() => {
    if (parsedInput.status !== 'valid') {
      setLoading(false);
      setResult(null);
      setMessage(parsedInput.status === 'invalid'
        ? { type: 'invalid', text: parsedInput.message }
        : null);
      return undefined;
    }

    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setMessage(null);

    const timer = window.setTimeout(async () => {
      try {
        const loaded = await getFit({
          system: 'ISO286',
          diameter: parsedInput.diameter,
          hole: parsedInput.hole,
          shaft: parsedInput.shaft,
        }, { signal: controller.signal });
        if (active) setResult(loaded);
      } catch (error) {
        if (!active || error?.code === 'ERR_CANCELED') return;
        setResult(null);
        if (error?.response?.status === 404) {
          setMessage({ type: 'notAvailable', text: 'Fit not available for this diameter.' });
        } else if (error?.response?.status === 400) {
          setMessage({
            type: 'invalid',
            text: error?.response?.data?.message || 'Check the fit designations.',
          });
        } else {
          setMessage({ type: 'error', text: 'The fit could not be loaded. Please try again.' });
        }
      } finally {
        if (active) setLoading(false);
      }
    }, REQUEST_DELAY_MS);

    return () => {
      active = false;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [parsedInput]);

  const partialGuidance = useMemo(() => {
    if (parsedInput.status !== 'incomplete') return '';
    if (diameter.trim() && hole.trim() && !shaft.trim()) {
      return 'Enter the mating shaft tolerance to calculate the fit.';
    }
    if (diameter.trim() && shaft.trim() && !hole.trim()) {
      return 'Enter the mating hole tolerance to calculate the fit.';
    }
    return 'Complete the diameter, hole, and shaft tolerances to calculate the fit.';
  }, [diameter, hole, parsedInput.status, shaft]);

  return (
    <main className="fitsPage">
      <header className="fitsPageHeader">
        <p>Tolerances · ISO 286</p>
        <h1>ISO 286 Fits Calculator</h1>
        <span>Enter known hole and shaft tolerance designations to inspect their dimensional relationship.</span>
      </header>

      <section className="fitCalculator" aria-labelledby="fit-calculator-title" ref={calculatorRef}>
        <header className="fitContentIntro">
          <p>Automatic calculation</p>
          <h2 id="fit-calculator-title">Fit Calculator</h2>
          <span>Enter the nominal diameter and both tolerance designations.</span>
        </header>

        <div className="fitInputs">
          <label htmlFor="fit-diameter">
            <span>Nominal diameter</span>
            <div className="fitInputWithUnit">
              <input
                id="fit-diameter"
                aria-label="Nominal diameter"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={diameter}
                onChange={(event) => setDiameter(event.target.value)}
                aria-describedby={message ? 'fit-input-message' : undefined}
              />
              <span>{unitLabel('mm', unitSystem)}</span>
            </div>
          </label>
          <label htmlFor="fit-hole">
            <span>Hole</span>
            <input
              id="fit-hole"
              type="text"
              autoComplete="off"
              spellCheck="false"
              value={hole}
              onChange={(event) => setHole(normalizeFitDesignationCase(event.target.value, 'hole'))}
              aria-describedby={message ? 'fit-input-message' : undefined}
            />
          </label>
          <label htmlFor="fit-shaft">
            <span>Shaft</span>
            <input
              id="fit-shaft"
              type="text"
              autoComplete="off"
              spellCheck="false"
              value={shaft}
              onChange={(event) => setShaft(normalizeFitDesignationCase(event.target.value, 'shaft'))}
              aria-describedby={message ? 'fit-input-message' : undefined}
            />
          </label>
        </div>

        <div className="fitCalculatorStatus" aria-live="polite">
          {loading && <p><CircleDot size={17} aria-hidden="true" /> Calculating fit…</p>}
          {!loading && !message && !result && partialGuidance && <p>{partialGuidance}</p>}
          {message && (
            <p id="fit-input-message" className={`fitMessage fitMessage--${message.type}`} role={message.type === 'error' ? 'alert' : 'status'}>
              {message.text}
            </p>
          )}
        </div>
        {result && <FitResult result={result} />}
      </section>

      <section className="fitsExplanation" aria-labelledby="fits-explanation-title">
        <p>Understanding ISO 286 fits</p>
        <h2 id="fits-explanation-title">How hole and shaft tolerances work together</h2>
        <p className="fitsExplanationLead">
          A fit describes the dimensional relationship between a mating hole and shaft.
          Their ISO 286 tolerance zones set the permitted size ranges around the nominal
          dimension, and the actual result therefore depends on the diameter entered.
        </p>
        <div>
          <article>
            <h3>Clearance fit</h3>
            <p>The smallest permitted hole is at least as large as the largest permitted shaft, so assembly clearance always remains.</p>
          </article>
          <article>
            <h3>Transition fit</h3>
            <p>The tolerance zones overlap, so the manufactured parts may assemble with either a small clearance or a small interference.</p>
          </article>
          <article>
            <h3>Interference fit</h3>
            <p>The smallest permitted shaft is at least as large as the largest permitted hole, producing a press or shrink fit.</p>
          </article>
        </div>
        <p className="fitsExplanationLead">
          A hole-basis system keeps the hole position constant while shaft positions vary;
          a shaft-basis system does the reverse. Minimum and maximum values describe the
          range possible across the two manufactured size limits.
        </p>
        <p className="fitsExplanationNote">Always confirm the selected fit against functional, manufacturing, material, surface-finish, and assembly requirements.</p>
      </section>
    </main>
  );
};

export default FitsPage;
